import React, { Dispatch, SetStateAction, useState } from 'react';
import { toast } from 'react-toastify';
import { getAllData } from '../../utils/indexedDB';
import Button from './Button';
import Card, { CardBody, CardHeader, CardTitle } from './Card';

const FileUploadAudio = ({
	file,
	setFile,
	isSubmitting,
}: {
	file: File | undefined;
	setFile: Dispatch<SetStateAction<File | undefined>>;
	isSubmitting: boolean;
}) => {
	const [isDragging, setIsDragging] = useState(false);
	const [tempFile, setTempFile] = useState<File | undefined>();

	const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
		const fileInput = e.target;
		const file = e.target.files?.[0];
		if (file && file.type.startsWith('audio/')) {
			const audio = document.createElement('audio');

			audio.preload = 'metadata';
			const data = await getAllData();
			if (data.length <= 2) {
				audio.onloadedmetadata = () => {
					window.URL.revokeObjectURL(audio.src);
					if (audio.duration < 180) {
						toast.error('Audio duration must be greater than 3 minutes');
					} else if (audio.duration > 3600) {
						toast.error('Audio duration must be less than 1 hour');
					} else {
						setTempFile(file);

					}
				};
				audio.src = URL.createObjectURL(file);
			} else {
				toast.error(`You can't upload file due to already 3 audios are working`);
			}
		} else {
			toast.error('Please Upload only audio file');
		}
		// Clear the file input value to allow re-uploading the same file
		fileInput.value = '';
	};

	const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
		e.preventDefault();
		if (!isDragging) setIsDragging(true);
	};

	const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
		e.preventDefault();
		setIsDragging(false);
	};

	const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
		e.preventDefault();
		setIsDragging(false);
		const files = e.dataTransfer.files;
		if (files.length > 0) {
			const file = files[0];
			if (file && file.type.startsWith('audio/')) {
				const audioElement = document.createElement('audio');

				audioElement.preload = 'metadata';

				audioElement.onloadedmetadata = function () {
					window.URL.revokeObjectURL(audioElement.src);
					const duration = audioElement.duration;

					if (duration < 180) {
						toast.error('Audio duration must be greater than 3 minutes');
					} else if (duration > 3600) {
						toast.error('Audio duration must be less than 1 hour');
					} else {
						setTempFile(file);
					}
				};

				audioElement.src = URL.createObjectURL(file);
			} else {
				toast.error('Please drop a valid audio file.');
			}
		}
	};

	const handleClick = () => {
		const fileInput = document.querySelector('input[type="file"]') as HTMLInputElement;
		fileInput?.click();
	};

	const handleRemoveFile = () => {
		setFile(undefined);
	};

	const handleSubmit = () => {
		if (tempFile) {
			setFile(tempFile);
			setTempFile(undefined); // Clear temp file after submit
		}
	};

	return (
		<Card className='mx-auto mb-16 mt-7 max-w-md shadow-lg'>
			<CardHeader>
				<CardTitle className='w-full justify-center text-center text-blue-950'>
					Upload Audio File
				</CardTitle>
			</CardHeader>
			<CardBody className='flex flex-col items-center gap-6 pb-8'>
				<div
					onDragOver={handleDragOver}
					onDragLeave={handleDragLeave}
					onDrop={handleDrop}
					className={`w-full cursor-pointer rounded-lg border-2 border-dashed p-8 text-center transition-all ${isDragging
						? 'border-blue-500 bg-blue-50'
						: file
							? 'border-green-500 bg-green-50'
							: 'border-gray-300 hover:border-blue-400 hover:bg-gray-50'
						}`}>
					<input
						type='file'
						accept='.webm'
						className='hidden'
						onChange={handleFileChange}
					/>


					{file || tempFile ? (
						<div className='flex flex-col items-center'>
							<div className='mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-green-100'>
								<svg
									xmlns='http://www.w3.org/2000/svg'
									className='h-6 w-6 text-green-600'
									fill='none'
									viewBox='0 0 24 24'
									stroke='currentColor'>
									<path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M5 13l4 4L19 7' />
								</svg>
							</div>
							<p className='mb-1 text-lg font-medium text-gray-800'>File Selected</p>
							<p className='mb-4 text-sm text-gray-500'>{(tempFile || file)?.name}</p>
							<div className='flex gap-3'>
								<Button
									variant='solid'
									colorIntensity='500'
									className='primary-bg min-w-32 font-medium text-white'
									size='sm'
									icon={isSubmitting ? undefined : 'check'}
									isDisable={isSubmitting}
									onClick={handleSubmit}>
									{isSubmitting ? 'Uploading...' : 'Submit'}
								</Button>
								<Button
									variant='outline'
									color='red'
									colorIntensity='500'
									size='sm'
									icon='trash'
									isDisable={isSubmitting}
									onClick={() => setTempFile(undefined)}>
									Cancel
								</Button>
							</div>
						</div>
					) : (
						<div className='flex flex-col items-center'>
							<div className='mb-3 flex h-16 w-16 items-center justify-center rounded-full bg-blue-100'>
								<svg
									xmlns='http://www.w3.org/2000/svg'
									className='primary-text h-8 w-8'
									fill='none'
									viewBox='0 0 24 24'
									stroke='currentColor'>
									<path
										strokeLinecap='round'
										strokeLinejoin='round'
										strokeWidth={2}
										d='M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3'
									/>
								</svg>
							</div>
							<p className='mb-2 text-lg font-medium text-gray-800'>
								Drag & Drop your audio file here
							</p>
							<p className='mb-4 text-sm text-gray-500'>
								Only Audio files are supported
							</p>
							<p className='mb-4 text-xs text-gray-400'>
								File must be between 3 minutes and 1 hour in length
							</p>
							<Button
								variant='solid'
								className='primary-bg'
								colorIntensity='500'
								icon='upload'
								onClick={handleClick}>
								Upload Audio
							</Button>
						</div>
					)}
				</div>
			</CardBody>
		</Card>
	);
};

export default FileUploadAudio;